import { useAuthUrl } from 'features/auth/hooks/useAuthUrl/useAuthUrl';
import fetchVerifyAttribute, {
  VerifyAttributeData,
  VerifyAttributeResponse,
} from 'features/auth/utils/fetchVerifyAttribute/fetchVerifyAttribute';
import {
  DEVICE_VERIFICATION_TYPE,
  EMAIL_VERIFICATION_TYPE,
  PHONE_VERIFICATION_TYPE,
} from 'features/signup/constants';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureFlagKey } from 'services/statsig/types';
import { Headers } from 'shared/constants/headers';
import { STORAGE_KEYS } from 'shared/constants/storage';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { getLambdaHeaders } from 'shared/hooks/auth/useLambdaHeaders';
import { useAuth } from 'shared/providers/auth';
import { setItem } from 'shared/utils/asyncStorage';
import {
  saveHasPermanentRefreshToken,
  saveRefreshTokenForReactNative,
} from 'shared/utils/biometricAuth/biometricAuth';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

// Error codes
export const EXPIRED_CODE_ERROR_CODE = 6;
export const MAX_LIMIT_REACHED_CODE = 4;
export const INCORRECT_CODE_ERROR_CODE = 5;
export const FAILURE_MESSAGES = {
  EXPIRED_CODE: 'expired_code',
  INCORRECT_CODE: 'incorrect_code',
  CHALLENGE_REDIRECT: 'challenge_redirect',
  UNKNOWN_ERROR: 'unknown_error',
};

export interface VerifyChallengeOptions {
  verifyType?: string;
}

export interface VerifyChallengeResult {
  success: boolean;
  errorCode?: number;
  message?: string;
  title?: string;
  data?: VerifyAttributeResponse;
  error?: Error;
}

const useVerifyChallenge = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Use ref to store custom failure reason without causing re-renders
  const failureReasonRef = useRef<string>('');

  const { getVaroSessionId, storeIdToken, fetchIovationToken } = useAuth();
  const authUrl = useAuthUrl(
    FeatureFlagKey.VERIFY_ATTRIBUTE_USE_K8S_AUTH_SERVICE,
  );
  const { t: translatedText } = useTranslation('otp');

  // Analytics hooks for verification events
  const logVerificationFailedEvent = usePressableAnalytics({
    analyticsId: 'verificationFailed',
    customAttributes: {
      // This getter will be evaluated when the analytics call happens
      get failure_reason() {
        return failureReasonRef.current || errorMessage || '';
      },
      verification_type: 'email',
    },
  });

  const logVerificationPhoneFailedEvent = usePressableAnalytics({
    analyticsId: 'phoneVerificationFailed',
    customAttributes: {
      get failure_reason() {
        return failureReasonRef.current || errorMessage || '';
      },
      verification_type: 'phone',
    },
  });

  const logVerificationDeviceFailedEvent = usePressableAnalytics({
    analyticsId: 'verificationFailed',
    customAttributes: {
      get failure_reason() {
        return failureReasonRef.current || errorMessage || '';
      },
      verification_type: 'device',
    },
  });

  const logVerificationSuccessfulEvent = usePressableAnalytics({
    analyticsId: 'verificationSuccessful',
    customAttributes: { verification_type: 'phone' },
  });

  const logVerificationSuccessfulDeviceEvent = usePressableAnalytics({
    analyticsId: 'verificationSuccessful',
    customAttributes: { verification_type: 'device' },
  });

  const logVerificationSuccessfulEmailEvent = usePressableAnalytics({
    analyticsId: 'verificationSuccessful',
    customAttributes: { verification_type: 'email' },
  });

  const logVerificationError = (
    error: Error,
    verificationType?: string,
    failureReason?: string,
  ) => {
    // Set the failure reason in the ref before calling analytics
    failureReasonRef.current = failureReason || '';

    switch (verificationType) {
      case PHONE_VERIFICATION_TYPE:
        logVerificationPhoneFailedEvent();
        break;
      case DEVICE_VERIFICATION_TYPE:
        logVerificationDeviceFailedEvent();
        break;
      case EMAIL_VERIFICATION_TYPE:
      default:
        logVerificationFailedEvent();
        break;
    }

    // Clear the ref after using it
    failureReasonRef.current = '';

    logBreadcrumb({
      level: 'error',
      message: `Signup OTP error: ${error.message}`,
    });
  };

  const logVerificationSuccess = (options?: VerifyChallengeOptions) => {
    switch (options?.verifyType) {
      case DEVICE_VERIFICATION_TYPE:
        logVerificationSuccessfulDeviceEvent();
        break;
      case EMAIL_VERIFICATION_TYPE:
        logVerificationSuccessfulEmailEvent();
        break;
      default:
        logVerificationSuccessfulEvent();
        break;
    }
  };

  const verifyChallenge = async (
    data: VerifyAttributeData,
    options?: VerifyChallengeOptions,
    headersProp?: Headers,
  ): Promise<VerifyChallengeResult> => {
    setIsLoading(true);
    setErrorMessage(null);
    setIsSuccess(false);

    try {
      const aToken = await fetchIovationToken();
      const varoSessionId = await getVaroSessionId();
      const headers =
        headersProp ?? (await getLambdaHeaders(undefined, varoSessionId));

      const verifyAttributeResult = await fetchVerifyAttribute(
        authUrl,
        { ...data, aToken },
        headers,
      );

      const { message, title, errorCode, temporary } = verifyAttributeResult;

      if (message) {
        setErrorMessage(message);
      }

      if (errorCode === EXPIRED_CODE_ERROR_CODE) {
        logVerificationError(
          new Error(message || 'Expired code'),
          options?.verifyType,
          FAILURE_MESSAGES.EXPIRED_CODE,
        );

        return {
          success: false,
          errorCode,
          message,
          title,
          data: verifyAttributeResult,
        };
      } else if (errorCode === INCORRECT_CODE_ERROR_CODE) {
        logVerificationError(
          new Error(message || 'Incorrect code'),
          options?.verifyType,
          FAILURE_MESSAGES.INCORRECT_CODE,
        );

        return {
          success: false,
          errorCode,
          message,
          title,
          data: verifyAttributeResult,
        };
      }

      if (
        verifyAttributeResult.refreshToken &&
        verifyAttributeResult.temporary !== undefined
      ) {
        void saveHasPermanentRefreshToken(!verifyAttributeResult.temporary);
        await setItem(
          STORAGE_KEYS.REFRESH_TOKEN,
          verifyAttributeResult.refreshToken,
        );
        if (!temporary && verifyAttributeResult.refreshToken) {
          await saveRefreshTokenForReactNative(
            verifyAttributeResult.refreshToken,
          );
        }
      }

      if (verifyAttributeResult.challenge) {
        logVerificationSuccess(options);

        return { success: false, data: verifyAttributeResult };
      } else if (verifyAttributeResult.idToken) {
        logVerificationSuccess(options);
        setIsSuccess(true);
        await storeIdToken(verifyAttributeResult.idToken);
        return { success: true, data: verifyAttributeResult };
      } else if (verifyAttributeResult.challengeVerified === 'true') {
        logVerificationSuccess(options);
        setIsSuccess(true);
        return { success: true, data: verifyAttributeResult };
      }

      // FAILURE: unknown_error (error_title, error_message, error_code)
      logVerificationError(
        new Error(message || 'Unknown error'),
        options?.verifyType,
        FAILURE_MESSAGES.UNKNOWN_ERROR,
      );

      return {
        success: false,
        errorCode,
        title: title || '',
        message: message,
        data: verifyAttributeResult,
      };
    } catch (error) {
      const typedError =
        error instanceof Error ? error : new Error(String(error));

      logVerificationError(
        typedError,
        options?.verifyType,
        FAILURE_MESSAGES.UNKNOWN_ERROR,
      );

      setErrorMessage(translatedText('genericError'));
      return {
        success: false,
        error: typedError,
        message: translatedText('genericError'),
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    verifyChallenge,
    isLoading,
    isSuccess,
    errorMessage,
    setErrorMessage,
  };
};

export default useVerifyChallenge;
